import { useEffect, useState } from 'react';
import { SCUIContent } from '../../atomic/organisms';
import { SCUIBreadCrumb } from '../../atomic/molecules';
import {
    Select,
    MenuItem,
    Checkbox,
    FormControl,
    InputLabel,
    FormGroup,
    Autocomplete,
    TextField,
    Box,
    Typography,
    Chip,
    Divider,
    Paper,
    Grid,
} from '@mui/material';
import { OperatorQuery, OrderQuery } from '../../services/query';
import { OrderCommand } from '../../services/command';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { SCUIButton } from '../../atomic/atoms';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ChecklistIcon from '@mui/icons-material/Checklist';
import SaveIcon from '@mui/icons-material/Save';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { SLSM, slsmCls } from './shippingListStatusManagement.classes';

const sectionLabelSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    mb: 2,
};

const inputSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 2,
        fontSize: 13,
        transition: 'all 0.2s ease',
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
    },
    '& .MuiInputLabel-root': { fontSize: 13, fontWeight: 500 },
};

function ShippingListStatusManagement() {
    const { t } = useTranslation();
    const [selectedPermissionStatus, setSelectedPermissionStatus] = useState<any>('');
    const [selectedValue, setSelectedValue] = useState<any>('');
    const [selectedOption, setSelectedOption] = useState<any>(null);
    const [statuses, setStatuses] = useState<any>();
    const [rolesList, setRolesList] = useState<any>();
    const [shippingListByRole, setShippingListByRole] = useState<any>();
    const [firstSelectboxState, setFirstSelectboxState] = useState<any>();
    const [selectedRoles, setSelectedRoles] = useState<any>([]);

    const handleChange = (event: any) => {
        setSelectedPermissionStatus(event.target.value);
    };
    useEffect(() => {
        const fetchData = async () => {
            await GetShippingListStatusFirstSelectBox();
        };
        getRolesList();
        fetchData();
    }, []);
    useEffect(() => {
        if (!shippingListByRole) {
            return;
        }
        const rolesWithPermission = Array.from(
            new Set((shippingListByRole?.data || []).map((item: any) => item.roleNameKey))
        );

        setSelectedRoles(rolesWithPermission);
    }, [shippingListByRole]);
    useEffect(() => {
        if (selectedPermissionStatus && selectedPermissionStatus !== '') {
            const req = {
                fromStatus: selectedValue,
                toStatus: [selectedPermissionStatus],
            };
            OrderQuery.GetShippingListByRole(req)
                .then((res) => {
                    setShippingListByRole(res);
                })
                .catch((error) => {
                    console.error(t('general.queryError'), error);
                });
        }
    }, [selectedPermissionStatus, selectedValue]);

    const GetShippingListStatusFirstSelectBox = async (req?: any) => {
        const culture = JSON.parse(localStorage.getItem('languageObject') || '{}')?.languageCode;
        let request = { shipmentId: req, cultureCode: culture };
        const res = await OrderQuery.GetShipmentStatusList(request);
        setFirstSelectboxState(res.data);
    };
    const GetShippingListStatusSecondSelectBox = async (req?: any) => {
        const culture = JSON.parse(localStorage.getItem('languageObject') || '{}')?.languageCode || 'tr-TR';
        let request = { shipmentId: req, cultureCode: culture };

        const response = await OrderQuery.GetShippingStatusesFromRuleServiceByShipmentId(request);
        const parseData = response?.data?.map((item: any) => {
            return { value: item.shipmentStatusId, label: item.statusDescription };
        });

        setStatuses(parseData);
        setSelectedPermissionStatus('');
    };

    const handleSelectChange = async (event: any, newValue: any) => {
        event.preventDefault();
        setSelectedOption(newValue);
        const statusId = newValue ? newValue.shipmentStatusId : '';
        setSelectedValue(statusId);

        if (statusId) {
            await GetShippingListStatusSecondSelectBox(statusId);
        }
    };

    const handleRoleChange = (event: any) => {
        const { name, checked } = event.target;
        if (checked) {
            setSelectedRoles([...selectedRoles, name]);
        } else {
            setSelectedRoles(selectedRoles.filter((role: any) => role !== name));
        }
    };

    const getRolesList = () => {
        OperatorQuery.GetRolesList()
            .then((res: any) => {
                if (res) {
                    let parseData = res.model?.data?.map((item: any) => {
                        return { roleId: item.roleId, roleNameKey: item.roleNameKey };
                    });
                    setRolesList(parseData);
                }
            })
            .catch((error: any) => {
                console.log('Hata :>> ', error);
            });
    };
    const handleSubmit = () => {
        const selectedRoleIds = selectedRoles
            ?.map((roleName: any) => {
                const role = rolesList?.find((r: any) => r.roleNameKey === roleName);
                return role ? role.roleId : null;
            })
            .filter((id: any) => id !== null);
        let uniqueRoleIds = selectedRoleIds.filter((roleId: any, index: any, array: any) => {
            return array.indexOf(roleId) === index;
        });
        let req = {
            roleId: uniqueRoleIds,
            fromStatusId: selectedValue,
            toStatusId: [selectedPermissionStatus],
            changedBy: 1,
        };
        OrderCommand.SaveShippingListStatusByRole(req).then(() => {
            Swal.fire(t('general.added'), ' ', 'success');
        });
    };

    const selectedCount = selectedRoles?.length ?? 0;

    const toggleRole = (roleNameKey: string) => {
        const isChecked = selectedRoles.includes(roleNameKey);
        handleRoleChange({ target: { name: roleNameKey, checked: !isChecked } });
    };

    return (
        <SCUIContent className={slsmCls(SLSM.page, SLSM.root)}>
            {/* Breadcrumb + Save button row */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }} className={SLSM.header}>
                <Grid container direction="row" spacing={2} sx={{ flex: 1 }} className={SLSM.breadcrumb}>
                    <SCUIBreadCrumb
                        title={t('shippingListStatusManagement.pageTitle')}
                        componentLink={'/shippingListStatusManagement'}
                    />
                </Grid>
            </Box>

            <Box
                sx={{
                    mt: 2,
                    display: 'flex',
                    flexDirection: 'row',
                }}
                className={SLSM.body}
            >
                <Paper
                    className={SLSM.panel}
                    elevation={0}
                    sx={{
                        border: '1px solid',
                        borderColor: 'grey.200',
                        borderRadius: 3,
                        overflow: 'hidden',
                        width: '70%',
                        marginRight: '16px',
                    }}
                >
                    <Box sx={{ px: 3, py: 3, display: 'flex', flexDirection: 'column', gap: 3 }} className={SLSM.panelBody}>
                        {/* From status */}
                        <Box className={slsmCls(SLSM.section, SLSM.sectionFromStatus)}>
                            <Box sx={sectionLabelSx} className={SLSM.sectionLabel}>
                                <LocalShippingIcon sx={{ fontSize: 17, color: 'primary.main' }} className={SLSM.sectionLabelIcon} />
                                <Typography
                                    variant="subtitle2"
                                    fontWeight={700}
                                    fontSize={13}
                                    color="text.primary"
                                    className={SLSM.sectionTitle}
                                >
                                    {t('shippingListStatusManagement.status')}
                                </Typography>
                            </Box>
                            {firstSelectboxState && (
                                <Autocomplete
                                    className={SLSM.inputFromStatus}
                                    data-selected-status-id={selectedValue}
                                    options={firstSelectboxState}
                                    getOptionLabel={(option: any) => option.statusDescription || ''}
                                    value={selectedOption}
                                    onChange={handleSelectChange}
                                    size="small"
                                    renderOption={(props, option: any) => (
                                        <li
                                            {...props}
                                            className={`${(props as any).className || ''} ${SLSM.fromStatusOption}`.trim()}
                                            data-shipment-status-id={option?.shipmentStatusId}
                                            data-status-description={option?.statusDescription}
                                        >
                                            {option.statusDescription || ''}
                                        </li>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={t('general.select')}
                                            variant="outlined"
                                            sx={inputSx}
                                        />
                                    )}
                                    isOptionEqualToValue={(option: any, value: any) =>
                                        option.shipmentStatusId === value?.shipmentStatusId
                                    }
                                    slotProps={{
                                        listbox: {
                                            style: { maxHeight: 48 * 4.5 + 8 },
                                        },
                                    }}
                                />
                            )}
                        </Box>

                        {/* To (allowed) status */}
                        {statuses && (
                            <>
                                <Box
                                    sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.disabled' }}
                                    className={SLSM.transitionDivider}
                                >
                                    <Divider sx={{ flex: 1 }} className={SLSM.transitionDividerLine} />
                                    <ArrowForwardIcon sx={{ fontSize: 18 }} className={SLSM.transitionDividerIcon} />
                                    <Divider sx={{ flex: 1 }} className={SLSM.transitionDividerLine} />
                                </Box>

                                <Box className={slsmCls(SLSM.section, SLSM.sectionAllowedStatus)}>
                                    <Box sx={sectionLabelSx} className={SLSM.sectionLabel}>
                                        <CheckCircleOutlineIcon
                                            sx={{ fontSize: 17, color: 'primary.main' }}
                                            className={SLSM.sectionLabelIcon}
                                        />
                                        <Typography
                                            variant="subtitle2"
                                            fontWeight={700}
                                            fontSize={13}
                                            color="text.primary"
                                            className={SLSM.sectionTitle}
                                        >
                                            {t('shippingListStatusManagement.allowedStatus')}
                                        </Typography>
                                    </Box>
                                    <FormControl fullWidth size="small" className={SLSM.allowedStatusControl}>
                                        <InputLabel id="multi-select-label">
                                            {t('shippingListStatusManagement.status')}
                                        </InputLabel>
                                        <Select
                                            className={SLSM.inputAllowedStatus}
                                            data-from-status-id={selectedValue}
                                            data-to-status-id={selectedPermissionStatus}
                                            labelId="multi-select-label"
                                            value={selectedPermissionStatus}
                                            onChange={handleChange}
                                            label={t('shippingListStatusManagement.status')}
                                            sx={{
                                                borderRadius: 2,
                                                fontSize: 13,
                                                transition: 'all 0.2s ease',
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'primary.main',
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    style: { maxHeight: 48 * 4.5 + 8, width: 280 },
                                                },
                                            }}
                                        >
                                            {statuses.map((item: any) => (
                                                <MenuItem
                                                    key={item.value}
                                                    value={item.value}
                                                    className={SLSM.allowedStatusOption}
                                                    data-status-id={item.value}
                                                    data-status-label={item.label}
                                                >
                                                    {item.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                            </>
                        )}

                        {/* Roles */}
                        <Divider />

                        <Box className={slsmCls(SLSM.section, SLSM.sectionRoles)}>
                            <Box
                                sx={{ ...sectionLabelSx, justifyContent: 'space-between', mb: 1.5 }}
                                className={SLSM.rolesHeader}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} className={SLSM.rolesHeaderTitle}>
                                    <ManageAccountsIcon
                                        sx={{ fontSize: 18, color: 'primary.main' }}
                                        className={SLSM.sectionLabelIcon}
                                    />
                                    <Typography
                                        variant="subtitle2"
                                        fontWeight={700}
                                        fontSize={13}
                                        color="text.primary"
                                        className={SLSM.sectionTitle}
                                    >
                                        {t('shippingListStatusManagement.roles')}
                                    </Typography>
                                </Box>
                                {selectedCount > 0 && (
                                    <Chip
                                        className={SLSM.selectedCountChip}
                                        data-selected-count={selectedCount}
                                        icon={<ChecklistIcon />}
                                        label={`${selectedCount} seçili`}
                                        size="small"
                                        sx={{
                                            fontSize: 11,
                                            height: 24,
                                            fontWeight: 700,
                                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                            color: 'primary.main',
                                            border: '1px solid rgba(25, 118, 210, 0.2)',
                                            '& .MuiChip-icon': { fontSize: 14, color: 'primary.main' },
                                        }}
                                    />
                                )}
                            </Box>

                            <Box
                                className={SLSM.rolesList}
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'grey.200',
                                    borderRadius: 2,
                                    maxHeight: 220,
                                    overflowY: 'auto',
                                    '&::-webkit-scrollbar': { width: 6 },
                                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                                    '&::-webkit-scrollbar-thumb': {
                                        background: 'rgba(0,0,0,0.15)',
                                        borderRadius: 3,
                                    },
                                }}
                            >
                                <FormControl component="fieldset" sx={{ width: '100%' }} className={SLSM.rolesFieldset}>
                                    <FormGroup className={SLSM.rolesGroup}>
                                        {rolesList &&
                                            rolesList.map((role: any, idx: number) => {
                                                const isChecked = selectedRoles.includes(role.roleNameKey);
                                                return (
                                                    <Box
                                                        key={idx}
                                                        className={slsmCls(SLSM.roleRow, isChecked && SLSM.roleRowChecked)}
                                                        data-role-id={role.roleId}
                                                        data-role-name={role.roleNameKey}
                                                        data-role-checked={String(isChecked)}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            px: 2,
                                                            py: 0.25,
                                                            borderBottom:
                                                                idx < rolesList.length - 1 ? '1px solid' : 'none',
                                                            borderColor: 'grey.100',
                                                            transition: 'background-color 0.15s ease',
                                                            backgroundColor: isChecked
                                                                ? 'rgba(25, 118, 210, 0.05)'
                                                                : 'transparent',
                                                            '&:hover': {
                                                                backgroundColor: isChecked
                                                                    ? 'rgba(25, 118, 210, 0.09)'
                                                                    : 'grey.50',
                                                            },
                                                            cursor: 'pointer',
                                                        }}
                                                        onClick={() => toggleRole(role.roleNameKey)}
                                                    >
                                                        <Checkbox
                                                            className={SLSM.inputRoleCheckbox}
                                                            data-role-id={role.roleId}
                                                            data-role-name={role.roleNameKey}
                                                            data-role-checked={String(isChecked)}
                                                            checked={isChecked}
                                                            onChange={handleRoleChange}
                                                            name={role.roleNameKey}
                                                            size="small"
                                                            icon={
                                                                <RadioButtonUncheckedIcon
                                                                    sx={{ fontSize: 18, color: 'grey.400' }}
                                                                />
                                                            }
                                                            checkedIcon={
                                                                <CheckCircleOutlineIcon
                                                                    sx={{ fontSize: 18, color: 'primary.main' }}
                                                                />
                                                            }
                                                            onClick={(e) => e.stopPropagation()}
                                                            sx={{ py: 0.5 }}
                                                        />
                                                        <Typography
                                                            className={SLSM.roleLabel}
                                                            data-role-id={role.roleId}
                                                            data-role-name={role.roleNameKey}
                                                            fontSize={12.5}
                                                            fontWeight={isChecked ? 700 : 400}
                                                            color={isChecked ? 'primary.main' : 'text.primary'}
                                                            sx={{
                                                                transition: 'all 0.15s ease',
                                                                flex: 1,
                                                                ml: 0.5,
                                                                userSelect: 'none',
                                                            }}
                                                            onClick={() => toggleRole(role.roleNameKey)}
                                                        >
                                                            {role.roleNameKey}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}
                                    </FormGroup>
                                </FormControl>
                            </Box>
                        </Box>
                    </Box>
                </Paper>
                <Box className={SLSM.actionBox}>
                    <SCUIButton
                        className={SLSM.btnSave}
                        onClick={handleSubmit}
                        type={'primary'}
                        sizeType={'fixed'}
                        size={'m'}
                        text={t('general.save')}
                        showStartIcon
                        startIcon={<SaveIcon style={{ fontSize: '16px' }} />}
                        style={{
                            borderRadius: '8px',
                            fontWeight: 700,
                            fontSize: '13px',
                            height: '40px',
                            minWidth: '120px',
                            flexShrink: 0,
                        }}
                    />
                </Box>
            </Box>
        </SCUIContent>
    );
}

export default ShippingListStatusManagement;
